import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { Icon } from "./icon";
import type { IconSymbol } from "./icon";

const toastVariants = cva(
  "flex items-center rounded-md border w-full shadow-toaster pr-2",
  {
    variants: {
      variant: {
        critical: "bg-red-50 border-red-500",
        warning: "bg-orange-50 border-orange-500",
        info: "bg-blue-50 border-blue-500",
        success: "bg-green-50 border-green-500",
        loading: "bg-blue-50 border-blue-500",
      },
    },
  },
);

const textVariants = cva("", {
  variants: {
    titleColor: {
      critical: "text-red-900",
      warning: "text-orange-900",
      info: "text-blue-800",
      success: "text-green-900",
      loading: "text-blue-800",
    },
    accentToastColor: {
      critical: "bg-error",
      warning: "bg-warning",
      info: "bg-info",
      success: "bg-success",
      loading: "bg-loading",
    },
  },
});

const iconMapping: Record<string, IconSymbol> = {
  critical: "octagon-x",
  warning: "triangle-alert",
  info: "circle-alert",
  success: "circle-check",
  loading: "loader-circle",
};

const separatorMapping: Record<string, string> = {
  critical: ",",
  warning: ",",
  info: " -",
  success: "",
  loading: " -",
};

export interface AlertCalloutProps {
  variant: "critical" | "warning" | "info" | "success" | "loading";
  title: string;
  description?: string;
  closeButtonSlot?: ReactNode;
  ariaLabel: string;
  className?: string;
  id?: string;
}

export const AlertCallout = ({
  variant,
  title,
  description,
  closeButtonSlot,
  ariaLabel,
  className,
  id,
}: AlertCalloutProps) => {
  const isLoading = variant === "loading";
  const titleId = id ? `${id}-title` : undefined;
  const descriptionId = description && id ? `${id}-description` : undefined;

  return (
    <div
      className={cn("flex", toastVariants({ variant }), className)}
      role={
        variant === "critical" || variant === "warning" ? "alert" : "status"
      }
      aria-live={
        variant === "critical" || variant === "warning" ? "assertive" : "polite"
      }
      aria-atomic="true"
      aria-busy={isLoading}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div
        className={cn(
          "text-white px-2 flex items-center justify-center rounded-l shrink-0 self-stretch",
          textVariants({ accentToastColor: variant }),
        )}
        aria-hidden="true"
      >
        <Icon
          icon={iconMapping[variant]}
          className={cn(isLoading && "animate-spin")}
          size={20}
          aria-hidden="true"
        />
      </div>
      <div className="w-full py-4 pl-4 pr-2">
        <p
          id={titleId}
          className={cn(
            "font-title !text-body",
            textVariants({ titleColor: variant }),
          )}
        >
          <span className="sr-only">{ariaLabel}: </span>
          {title}
          {description && (
            <span id={descriptionId}>
              {separatorMapping[variant]}
              <span className="text-caption ml-1 font-base">{description}</span>
            </span>
          )}{" "}
        </p>
      </div>

      {closeButtonSlot}
    </div>
  );
};
